
/**
 * FitnessCache centralizes optional memoization for fitness values.
 * - Supports key-based Map via `fitnessKey`
 * - Falls back to reference-identity WeakMap for object genomes
 * - No-ops when disabled
 */
export interface FitnessCacheOptions<T> {
  enabled: boolean
  fitnessKey?: (g: Readonly<T>) => string
}

export class FitnessCache<T> {
  private byKey?: Map<string, number>
  private byRef?: WeakMap<object, number>
  private keyFn?: (g: Readonly<T>) => string

  constructor(opts: FitnessCacheOptions<T>) {
    if (!opts.enabled) return
    if (opts.fitnessKey) {
      this.keyFn = opts.fitnessKey
      this.byKey = new Map<string, number>()
    } else {
      this.byRef = new WeakMap<object, number>()
    }
  }

  get(g: Readonly<T>): number | undefined {
    if (this.keyFn && this.byKey) {
      try {
        const k = this.keyFn(g)
        return this.byKey.get(k)
      } catch {
        return undefined
      }
    }
    if (this.byRef && typeof g === "object" && g !== null) {
      return this.byRef.get(g as unknown as object)
    }
    return undefined
  }

  set(g: Readonly<T>, f: number): void {
    if (!Number.isFinite(f)) return
    if (this.keyFn && this.byKey) {
      try {
        const k = this.keyFn(g)
        this.byKey.set(k, f)
      } catch {
        // ignore
      }
      return
    }
    if (this.byRef && typeof g === "object" && g !== null) {
      this.byRef.set(g as unknown as object, f)
    }
  }
}
